/**
 * <pullquote-slideshow>: cross-fades the quotes of the Pull-quote slideshow block
 * (blocks/ai_gen_block_470a8ec.liquid, children in blocks/_pull-quote-slide.liquid).
 *
 * Defined once for every slideshow on the page. It reads everything from its own markup (the quote
 * children, the arrows and dots container, data-autoplay and data-autoplay-speed), so it works for quotes
 * the theme editor adds, deletes or reorders without a reload:
 * - the quote list is read from the DOM again whenever it changes (a MutationObserver), and the shown
 *   quote, the dots and the autoplay timer are brought back in line with it;
 * - clicks and editor events are handled on the element itself, so markup that is replaced or moved
 *   needs no listeners of its own;
 * - connecting twice (the editor moves sections) is safe.
 */
class PullquoteSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = [];
    this.currentSlide = 0;
    this.autoplayInterval = null;
    /** The element the theme editor has selected inside this slideshow. Autoplay waits while it is set. */
    this.editorSelection = null;
    this.observer = new MutationObserver(() => this.reconcile());

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('.ai-pullquote-slideshow__prev')) this.previousSlide();
      else if (button.matches('.ai-pullquote-slideshow__next')) this.nextSlide();
      else if (button.matches('.ai-pullquote-slideshow__dot')) {
        this.goToSlide([...button.parentElement.children].indexOf(button));
      }
    });

    // Theme editor: selecting a quote shows it and holds autoplay; deselecting lets autoplay carry on.
    // The selection is remembered by block id, because the editor replaces a block's element each time
    // one of its settings changes.
    this.addEventListener('shopify:block:select', (event) => {
      this.editorSelection = event.detail?.blockId ?? event.target;
      this.stopAutoplay();
      this.reconcile();
      const index = this.slides.indexOf(event.target);
      if (index >= 0) this.goToSlide(index, { keepStopped: true });
    });
    this.addEventListener('shopify:block:deselect', (event) => {
      if (this.editorSelection !== (event.detail?.blockId ?? event.target)) return;
      this.editorSelection = null;
      this.startAutoplay();
    });
    this.addEventListener('shopify:block:load', () => this.reconcile());
    this.addEventListener('shopify:block:unload', () => queueMicrotask(() => this.reconcile()));
  }

  connectedCallback() {
    this.observer.observe(this, { childList: true, subtree: true });
    this.reconcile();
    // The block's CSS shows the first quote until this is set (see "data-ready" there).
    this.setAttribute('data-ready', '');
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.observer.disconnect();
    this.stopAutoplay();
  }

  get dotsContainer() {
    return this.querySelector('.ai-pullquote-slideshow__dots');
  }

  /** Match what is shown, the dots and the timer to the quotes that are in the markup now. */
  reconcile() {
    const slides = [...this.querySelectorAll('.ai-pullquote-slideshow__slide')];
    const changed = slides.length !== this.slides.length || slides.some((slide, i) => slide !== this.slides[i]);

    if (changed) {
      // Stay on the quote that was showing when it is still there (a reorder), otherwise on the same
      // position, so deleting or adding a quote does not jump the preview back to the first one.
      const kept = slides.indexOf(this.slides[this.currentSlide]);
      this.currentSlide = kept >= 0 ? kept : Math.min(this.currentSlide, Math.max(slides.length - 1, 0));
      this.slides = slides;
    }

    this.slides.forEach((slide, i) => slide.classList.toggle('active', i === this.currentSlide));

    const dots = this.dotsContainer;
    if (dots && (changed || dots.children.length !== this.slides.length)) this.createDots(dots);
    else if (dots) [...dots.children].forEach((dot, i) => dot.classList.toggle('active', i === this.currentSlide));

    if (changed) this.startAutoplay();
  }

  createDots(container) {
    container.replaceChildren(
      ...this.slides.map((slide, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ai-pullquote-slideshow__dot';
        if (index === this.currentSlide) dot.classList.add('active');
        dot.setAttribute('aria-label', `Go to quote ${index + 1}`);
        return dot;
      })
    );
  }

  goToSlide(index, { keepStopped = false } = {}) {
    if (!this.slides.length) return;
    this.currentSlide = index;
    this.reconcile();
    if (!keepStopped) this.startAutoplay();
  }

  nextSlide() {
    this.goToSlide((this.currentSlide + 1) % this.slides.length);
  }

  previousSlide() {
    this.goToSlide((this.currentSlide - 1 + this.slides.length) % this.slides.length);
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.editorSelection) return;
    const speed = parseInt(this.dataset.autoplaySpeed) || 5000;
    this.autoplayInterval = setInterval(() => this.nextSlide(), speed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }
}

if (!customElements.get('pullquote-slideshow')) {
  customElements.define('pullquote-slideshow', PullquoteSlideshow);
}
